import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { opportunitiesAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import ConfirmModal from '../components/ConfirmModal';
import { Briefcase, Plus, Edit, Trash2, Eye, Award } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminOpportunities: React.FC = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, scholarships: 0, internships: 0 });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const res = await opportunitiesAPI.getAll({ status: '', limit: 1000 });
      const data = res.data.data;
      setOpportunities(data);
      setStats({
        total: data.length,
        active: data.filter((o: any) => o.status === 'active').length,
        scholarships: data.filter((o: any) => o.type === 'scholarship').length,
        internships: data.filter((o: any) => o.type === 'internship').length,
      });
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Opportunity',
      message: 'Are you sure you want to delete this opportunity? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await opportunitiesAPI.delete(id);
          toast.success('Opportunity deleted');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchOpportunities();
        } catch (error) {
          toast.error('Failed to delete');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const filtered = opportunities.filter(o =>
    o.title.toLowerCase().includes(filter.toLowerCase()) ||
    o.organization?.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <AdminLayout><div className="p-8">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Opportunities</h1>
          <button
            onClick={() => navigate('/admin/opportunities/create')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} /> Add Opportunity
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Briefcase className="text-blue-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Eye className="text-green-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Active</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Award className="text-purple-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Scholarships</p>
                <p className="text-2xl font-bold">{stats.scholarships}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Briefcase className="text-orange-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Internships</p>
                <p className="text-2xl font-bold">{stats.internships}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <input
              type="text"
              placeholder="Search opportunities..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((opp) => (
                  <tr key={opp._id}>
                    <td className="px-6 py-4">
                      <div className="font-medium">{opp.title}</div>
                      {opp.featured && <span className="text-xs text-yellow-600">⭐ Featured</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {opp.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{opp.organization || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        opp.status === 'active' ? 'bg-green-100 text-green-800' :
                        opp.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {opp.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {opp.deadline ? new Date(opp.deadline).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">{opp.views}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/admin/opportunities/edit/${opp._id}`)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(opp._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          loading={processing}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminOpportunities;
